import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Building2, 
  ChevronDown,
  MapPin,
  Phone,
  ExternalLink
} from 'lucide-react';
import HotelDetailsModal from './modals/HotelDetailsModal';

// Helper to format booking dates cleanly as "12 Nov"
const formatBookingDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const HotelBookingsCard = ({ voucher, onUpdateVoucher }) => {
  const hotels = voucher?.hotel_bookings || [];
  
  // Specific Hotel Edit Modal state
  const [selectedHotelData, setSelectedHotelData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // View All Stays Modal state
  const [showAllHotels, setShowAllHotels] = useState(false);

  // Prevent background scrolling and enable Escape close when "View All Stays" modal is active
  useEffect(() => {
    if (!showAllHotels) return;
    const prevBodyOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('main');
    const prevMainOverflow = mainEl ? mainEl.style.overflow : '';

    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowAllHotels(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAllHotels]);

  const handleOpenHotelModal = (hotel, index) => {
    setSelectedHotelData({ hotel, index });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHotelData(null);
  };

  const handleSaveHotel = async (hotelIndex, updatedHotel) => {
    const updatedHotels = [...hotels];
    updatedHotels[hotelIndex] = updatedHotel;

    const updatedVoucher = {
      ...voucher,
      hotel_bookings: updatedHotels
    };

    if (onUpdateVoucher) {
      await onUpdateVoucher(updatedVoucher);
    }

    // Keep active modal updated with newest saved hotel object
    setSelectedHotelData({ hotel: updatedHotel, index: hotelIndex });
  };

  return (
    <>
      <div className="vc-card flex flex-col justify-between">
        <div>
          {/* Header */}
          <div className="vc-card-head flex items-center justify-between pb-2 border-b border-slate-100">
            <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
              <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                <Building2 size={15} className="text-[#067978]" />
              </span>
              <span>Hotel Bookings</span>
            </h3>
          </div>

          {/* Hotel Rows: Matches DMC layout */}
          <div className="divide-y divide-slate-100 mt-1">
            {hotels.length > 0 ? (
              hotels.slice(0, 3).map((hotel, idx) => {
                const hotelKey = hotel._id || hotel.id || idx;
                const hotelTitle = hotel.hotel_name || hotel.name || `Hotel #${idx + 1}`;
                const formattedDate = formatBookingDate(hotel.check_in_date);
                const timeStr = hotel.check_in_time || '14:00';
                const dateTimeString = formattedDate ? `${formattedDate} · ${timeStr}` : timeStr;
                const locationText = hotel.city || hotel.destination || hotel.hotel_address || hotel.address || '';

                return (
                  <div
                    key={hotelKey}
                    onClick={() => handleOpenHotelModal(hotel, idx)}
                    className="flex items-center justify-between py-2.5 px-0 hover:bg-slate-50/50 transition-colors cursor-pointer group select-none"
                    title="Click to view & edit hotel details"
                  >
                    <div className="min-w-0 pr-4">
                      <div className="text-[13px] sm:text-[14px] font-medium text-slate-700 group-hover:text-[#067978] transition-colors truncate">
                        {hotelTitle}
                      </div>
                      <div className="text-xs text-slate-500 font-normal mt-0.5 truncate">
                        {locationText || 'Stay details'}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-slate-500 font-normal">
                        {dateTimeString}
                      </span>
                      <ChevronDown size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-6 text-center text-xs text-slate-400">
                No hotel bookings attached to this voucher.
              </div>
            )}
          </div>
        </div>

        {/* View stays footer cue: "View 2 stays" (no "all") */}
        {hotels.length > 0 && (
          <div 
            className="vc-expand-cue mt-auto pt-3 flex items-center justify-center gap-1 text-xs font-normal text-[#067978] cursor-pointer hover:opacity-80 select-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAllHotels(true);
            }}
            title="View stays in full modal"
          >
            <span>{`View ${hotels.length} ${hotels.length === 1 ? 'stay' : 'stays'}`}</span>
            <ChevronDown size={14} className="text-[#067978]" />
          </div>
        )}

        {/* Individual Hotel Details & Edit Modal */}
        <HotelDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          hotel={selectedHotelData?.hotel}
          hotelIndex={selectedHotelData?.index}
          onSaveHotel={handleSaveHotel}
        />
      </div>

      {/* View All Stays Modal */}
      {typeof document !== 'undefined' && hotels.length > 0 && createPortal(
        <div
          className={`vc-overlay ${showAllHotels ? 'vc-open' : ''}`}
          style={{ top: 'var(--portal-header-height, 64px)', height: 'calc(100vh - var(--portal-header-height, 64px))' }}
          onClick={(e) => {
            if (e.target.classList.contains('vc-overlay')) setShowAllHotels(false);
          }}
        >
          <div className="vc-modal !max-w-2xl" role="dialog" aria-modal="true" aria-label="Hotel bookings">
            <div className="vc-m-head">
              <h4 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Building2 size={18} className="text-[#067978]" /> Hotel Bookings
              </h4>
              <button className="vc-m-close" onClick={() => setShowAllHotels(false)}>✕</button>
            </div>
            <div className="vc-m-body">
              {hotels.map((h, i) => (
                <div className="vc-stay" key={i}>
                  <div className="vc-stay-top flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-3 border-b border-slate-100">
                    <div className="min-w-0 flex-1">
                      {/* Hotel Name: moderate emphasis, not excessively bold */}
                      <div className="text-sm sm:text-base font-medium text-slate-800 break-words">
                        {h.hotel_name || h.name || `Hotel #${i + 1}`}
                      </div>
                      {/* Location: regular / muted */}
                      <div className="text-xs font-normal text-slate-500 flex items-center flex-wrap gap-1.5 mt-1">
                        <span className="flex items-center gap-1 text-[#067978] shrink-0 font-normal">
                          <MapPin size={13} />
                          {h.city || h.destination || "Location"}
                        </span>
                        {(h.hotel_address || h.address) && (
                          <span className="text-slate-400 font-normal break-words">· {h.hotel_address || h.address}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {h.hotel_phone && (
                        <a
                          href={`tel:${h.hotel_phone}`}
                          className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] bg-[#e6f3ec] hover:bg-[#cfe6da] px-2.5 py-1 rounded-lg transition-colors"
                        >
                          <Phone size={12} /> {h.hotel_phone}
                        </a>
                      )}
                      {h.map_link && (
                        <a
                          href={h.map_link.startsWith('http') ? h.map_link : `https://${h.map_link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] bg-[#e6f3ec] hover:bg-[#cfe6da] px-2.5 py-1 rounded-lg transition-colors"
                        >
                          <ExternalLink size={12} /> Map
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Refined Typography Grid: Small muted labels, regular/medium values */}
                  <div className="vc-stay-grid mt-3">
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Check-in</div>
                      <div className="vc-v !text-xs !font-medium !text-slate-700">
                        {h.check_in_date || "N/A"}
                        {h.check_in_time ? ` · ${h.check_in_time}` : ""}
                      </div>
                    </div>
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Check-out</div>
                      <div className="vc-v !text-xs !font-medium !text-slate-700">
                        {h.check_out_date || "N/A"}
                        {h.check_out_time ? ` · ${h.check_out_time}` : ""}
                      </div>
                    </div>
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Duration / Rooms</div>
                      <div className="vc-v !text-xs !font-medium !text-slate-700">
                        {h.no_of_nights ? `${h.no_of_nights} Nights` : 'N/A'} · {h.no_of_rooms || 1}{' '}
                        {h.no_of_rooms > 1 ? 'Rooms' : 'Room'}
                      </div>
                    </div>
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Meal plan</div>
                      {/* Meal plan: regular text */}
                      <div className="vc-v !text-xs !font-normal !text-slate-600">{h.meal_plan || "Room Only"}</div>
                    </div>
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Category</div>
                      {/* Category: regular/medium */}
                      <div className="vc-v !text-xs !font-medium !text-slate-700">{h.room_category || "Standard"}</div>
                    </div>
                    <div className="vc-sg">
                      <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Confirmation</div>
                      {/* Confirmation: regular/medium */}
                      <div className="vc-v vc-mono !text-xs !font-mono !font-medium !text-slate-700">{h.confirmation_number || "Pending"}</div>
                    </div>
                    {h.total_guests && (
                      <div className="vc-sg">
                        <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">Guests</div>
                        <div className="vc-v !text-xs !font-medium !text-slate-700">{h.total_guests}</div>
                      </div>
                    )}
                    {h.custom_fields &&
                      h.custom_fields.length > 0 &&
                      h.custom_fields.map((cf, cIdx) => (
                        <div className="vc-sg" key={cIdx}>
                          <div className="vc-l !text-[10px] !font-normal !text-slate-400 !uppercase !tracking-wider">{cf.label || "Custom Field"}</div>
                          <div className="vc-v !text-xs !font-medium !text-slate-700">{cf.value || "N/A"}</div>
                        </div>
                      ))}
                  </div>

                  {h.inclusions && (
                    <div className="mt-3 pt-2.5 border-t border-slate-100 text-xs">
                      <span className="font-medium text-slate-500 uppercase tracking-wider text-[10px]">
                        Inclusions:{' '}
                      </span>
                      <span className="text-slate-600 font-normal">{h.inclusions}</span>
                    </div>
                  )}
                </div>
              ))}

              {hotels.length === 0 && (
                <div className="text-sm text-[#8a9aa5] text-center py-6">No hotel bookings found.</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HotelBookingsCard;
